export const WORKSPACE_TEMPLATE_KEYS = [
    'newWorkspace',
    'assetManagerRia',
    'hedgeFund',
    'familyOffice',
    'endowment',
    'sponsoredResearch',
    'corporateDevelopment',
    'higherEducation',
] as const;

export type WorkspaceTemplateKey = (typeof WORKSPACE_TEMPLATE_KEYS)[number];

export interface WorkspaceTemplateOption {
    description: string;
    key: WorkspaceTemplateKey;
    label: string;
}

export const WORKSPACE_TEMPLATE_OPTIONS = [
    {
        key: 'newWorkspace',
        label: 'New Workspace',
        description: 'Start with the platform defaults and build a fully custom workspace from scratch.',
    },
    {
        key: 'assetManagerRia',
        label: 'Asset Management / RIA',
        description: 'Organize portfolio research, holdings oversight, client-ready analysis, and ongoing monitoring.',
    },
    {
        key: 'hedgeFund',
        label: 'Hedge Fund',
        description: 'Move quickly from idea generation to deep research, catalyst tracking, and portfolio monitoring.',
    },
    {
        key: 'familyOffice',
        label: 'Family Office',
        description: 'Coordinate multi-asset research, direct investments, manager diligence, and long-term stewardship.',
    },
    {
        key: 'endowment',
        label: 'Endowment',
        description: 'Coordinate manager research, asset allocation, portfolio oversight, and long-horizon stewardship.',
    },
    {
        key: 'sponsoredResearch',
        label: 'Sponsored Research',
        description: 'Structure company-sponsored coverage, source materials, KPI tracking, and publication workflows.',
    },
    {
        key: 'corporateDevelopment',
        label: 'M&A / Corporate Development',
        description: 'Manage target screening, strategic fit, transaction diligence, and decision-ready materials.',
    },
    {
        key: 'higherEducation',
        label: 'Higher Education',
        description: 'Organize coursework, collaborative research, source review, and student investment analysis.',
    },
] as const satisfies readonly WorkspaceTemplateOption[];

const workspaceTemplateKeySet = new Set<string>(WORKSPACE_TEMPLATE_KEYS);

export const isWorkspaceTemplateKey = (value: unknown): value is WorkspaceTemplateKey =>
    typeof value === 'string' && workspaceTemplateKeySet.has(value);

export const parseWorkspaceTemplateKey = (value: unknown): WorkspaceTemplateKey =>
    isWorkspaceTemplateKey(value) ? value : 'newWorkspace';

export const getStoredWorkspaceTemplateKey = (): WorkspaceTemplateKey => {
    const storedValue = localStorage.getItem('workspaceTemplate');

    if (!storedValue) {
        return 'newWorkspace';
    }

    try {
        return parseWorkspaceTemplateKey(JSON.parse(storedValue));
    } catch {
        return 'newWorkspace';
    }
};
